import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Plus, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Vendor {
  id: string;
  vendor_name: string;
  vendor_risk_level: string;
  technology_category: string | null;
  country_of_origin: string | null;
}

interface SelectedVendor {
  vendor_id: string;
  vendor_name: string;
  vendor_risk_level: string;
  criticality: string;
}

interface Props {
  assessmentId: string;
  readOnly?: boolean;
  onVendorCountChange?: (count: number) => void;
}

const riskDot = (level: string) =>
  level === "High" ? "🔴" : level === "Moderate" ? "🟡" : "🟢";

export function VendorSelectionSection({ assessmentId, readOnly, onVendorCountChange }: Props) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendors, setSelectedVendors] = useState<SelectedVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingVendorId, setAddingVendorId] = useState<string>("");
  const { toast } = useToast();

  // Load vendor registry + existing selections
  useEffect(() => {
    async function load() {
      setLoading(true);

      // Verify auth session before querying
      const { data: sessionData } = await supabase.auth.getSession();
      const hasSession = !!sessionData?.session;
      console.log("[VendorSelection] Auth session present:", hasSession);

      const [regRes, selRes] = await Promise.all([
        supabase.rpc("get_active_vendors"),
        supabase.from("assessment_vendors").select("vendor_id, criticality").eq("assessment_id", assessmentId),
      ]);

      console.log("[VendorSelection] get_active_vendors RPC result:", {
        count: regRes.data?.length ?? 0,
        error: regRes.error?.message ?? null,
      });

      if (regRes.error) {
        console.error("[VendorSelection] get_active_vendors RPC error:", regRes.error);
      }
      if (regRes.data) {
        const vendors = regRes.data || [];
        if (vendors.length === 0) {
          console.warn("[VendorSelection] No vendors returned from get_active_vendors RPC — table may need seeding");
        }
        setVendors(regRes.data as Vendor[]);
      }

      if (selRes.error) {
        console.error("[VendorSelection] assessment_vendors query error:", selRes.error);
      }
      if (selRes.data && regRes.data) {
        const regMap = new Map((regRes.data as Vendor[]).map((v) => [v.id, v]));
        const mapped = (selRes.data as any[]).map((r) => {
          const vendor = regMap.get(r.vendor_id);
          return {
            vendor_id: r.vendor_id,
            vendor_name: vendor?.vendor_name ?? "Unknown",
            vendor_risk_level: vendor?.vendor_risk_level ?? "Low",
            criticality: r.criticality ?? "Moderate",
          };
        });
        setSelectedVendors(mapped);
      }
      setLoading(false);
    }
    load();
  }, [assessmentId]);

  // Notify parent of vendor count changes
  useEffect(() => {
    onVendorCountChange?.(selectedVendors.length);
  }, [selectedVendors.length, onVendorCountChange]);

  const handleAddVendor = useCallback(async () => {
    if (!addingVendorId || readOnly) return;
    if (selectedVendors.some((v) => v.vendor_id === addingVendorId)) {
      toast({ title: "Already added", description: "This vendor is already selected." });
      return;
    }

    const vendor = vendors.find((v) => v.id === addingVendorId);
    if (!vendor) return;

    console.log("[VendorSelection] Adding vendor:", { assessmentId, vendorId: addingVendorId });
    const { error } = await supabase.rpc("add_vendor_to_assessment", {
      p_assessment_id: assessmentId,
      p_vendor_id: addingVendorId,
    });

    if (error) {
      console.error("[VendorSelection] insert error:", error);
      toast({ variant: "destructive", title: "Failed to add vendor" });
      return;
    }

    setSelectedVendors((prev) => [
      ...prev,
      {
        vendor_id: vendor.id,
        vendor_name: vendor.vendor_name,
        vendor_risk_level: vendor.vendor_risk_level,
        criticality: "Moderate",
      },
    ]);
    setAddingVendorId("");
  }, [addingVendorId, assessmentId, readOnly, selectedVendors, vendors, toast]);

  const handleRemoveVendor = useCallback(
    async (vendorId: string) => {
      if (readOnly) return;
      const { error } = await supabase
        .from("assessment_vendors")
        .delete()
        .eq("assessment_id", assessmentId)
        .eq("vendor_id", vendorId);

      if (error) {
        toast({ variant: "destructive", title: "Failed to remove vendor" });
        return;
      }
      setSelectedVendors((prev) => prev.filter((v) => v.vendor_id !== vendorId));
    },
    [assessmentId, readOnly, toast]
  );

  const handleCriticalityChange = useCallback(
    async (vendorId: string, criticality: string) => {
      if (readOnly) return;
      const { error } = await supabase
        .from("assessment_vendors")
        .update({ criticality })
        .eq("assessment_id", assessmentId)
        .eq("vendor_id", vendorId);

      if (error) {
        toast({ variant: "destructive", title: "Failed to update criticality" });
        return;
      }
      setSelectedVendors((prev) =>
        prev.map((v) => (v.vendor_id === vendorId ? { ...v, criticality } : v))
      );
    },
    [assessmentId, readOnly, toast]
  );

  if (loading) {
    return (
      <Card className="mt-6">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const availableVendors = vendors.filter(
    (v) => !selectedVendors.some((sv) => sv.vendor_id === v.id)
  );

  return (
    <Card className="mt-6 border-orange-200 dark:border-orange-800">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          🏢 Critical Technology Vendors
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Select the critical technology vendors used in your organization's infrastructure.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add vendor row */}
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Select value={addingVendorId} onValueChange={setAddingVendorId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a vendor to add..." />
              </SelectTrigger>
              <SelectContent>
                {availableVendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    <span className="flex items-center gap-2">
                      <span>{riskDot(v.vendor_risk_level)}</span>
                      <span>{v.vendor_name}</span>
                      {v.technology_category && (
                        <span className="text-xs text-muted-foreground">({v.technology_category})</span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={handleAddVendor} disabled={!addingVendorId}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        )}

        {/* Selected vendors list */}
        {selectedVendors.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No vendors selected yet.</p>
        ) : (
          <div className="space-y-2">
            {selectedVendors.map((sv) => (
              <div
                key={sv.vendor_id}
                className="flex items-center justify-between rounded-lg border px-3 py-2 bg-muted/30"
              >
                <div className="flex items-center gap-2">
                  <span>{riskDot(sv.vendor_risk_level)}</span>
                  <span className="font-medium text-sm">{sv.vendor_name}</span>
                  <Badge
                    variant="outline"
                    className={
                      sv.vendor_risk_level === "High"
                        ? "border-red-300 text-red-700"
                        : sv.vendor_risk_level === "Moderate"
                        ? "border-yellow-300 text-yellow-700"
                        : "border-green-300 text-green-700"
                    }
                  >
                    {sv.vendor_risk_level} Risk Vendor
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={sv.criticality}
                    onValueChange={(val) => handleCriticalityChange(sv.vendor_id, val)}
                    disabled={readOnly}
                  >
                    <SelectTrigger className="w-[120px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High Criticality</SelectItem>
                      <SelectItem value="Moderate">Moderate</SelectItem>
                      <SelectItem value="Low">Low Criticality</SelectItem>
                    </SelectContent>
                  </Select>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleRemoveVendor(sv.vendor_id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
